import './Passbook.css';
import { useState, useEffect, useContext, useRef } from 'react';
import { WalletContext } from './WalletContext';

const ITEMS_PER_PAGE = 15;

const DEBIT_TYPES = new Set(['withdrawal', 'bidplaced', 'loss']);

function isDebit(type, txData = {}) {
  // amount_transactions are credits (deposits)
  if (txData._type === 'amount') {
    return false;
  }
  return DEBIT_TYPES.has(type?.toLowerCase());
}

function formatDateTime(dateString) {
  try {
    const d = new Date(dateString);
    const day = d.getDate().toString().padStart(2, '0');
    const month = d.toLocaleString('en-US', { month: 'short' });
    const year = d.getFullYear();
    let hours = d.getHours();
    const minutes = d.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'pm' : 'am';
    hours = hours % 12 || 12;
    return `${day} ${month} ${year}, ${hours}:${minutes} ${ampm}`;
  } catch {
    return 'N/A';
  }
}

function buildPageNumbers(current, total) {
  if (total <= 1) return [];
  const pages = [];
  const rangeStart = Math.max(2, current - 1);
  const rangeEnd = Math.min(total - 1, current + 1);
  pages.push(1);
  if (rangeStart > 2) pages.push('…');
  for (let i = rangeStart; i <= rangeEnd; i++) pages.push(i);
  if (rangeEnd < total - 1) pages.push('…');
  if (total > 1) pages.push(total);
  return pages;
}

function getBadgeClass(status, txData = {}) {
  // Handle amount_transactions status (0 or 1)
  if (txData._type === 'amount') {
    if (status === 1) return 'approved';
    if (status === 0) return 'pending';
    return 'default';
  }

  // Handle user_transactions status
  const s = status?.toLowerCase();
  if (s === 'pending') return 'pending';
  if (s === 'approved' || s === 'success') return 'approved';
  if (s === 'rejected' || s === 'failed') return 'rejected';
  return 'default';
}

function getTypeLabel(type, txData = {}) {
  // For amount_transactions (payment deposits)
  if (txData._type === 'amount') {
    return 'Deposit';
  }
  
  if (!type) return 'Transaction';
  const map = {
    deposit: 'Add Funds',
    bonus: 'Bonus',
    withdrawal: 'Withdrawal',
    winning: 'Winning',
    loss: 'Loss',
    refund: 'Refund',
    bidplaced: 'Bid Placed',
    add: 'Extra Credits',
  };
  return map[type.toLowerCase()] || (type.charAt(0).toUpperCase() + type.slice(1));
}

// Get the transaction type for filtering/display
function getTransactionType(tx) {
  if (tx._type === 'amount') {
    return 'deposit'; // amount_transactions are treated as deposits
  }
  return tx.transaction_type;
}

// Down-arrow icon for credit, up-arrow icon for debit
function ArrowIcon({ debit }) {
  return debit ? (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
      <path d="M12 19V5M5 12l7-7 7 7" stroke="#c62828" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ) : (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
      <path d="M12 5v14M19 12l-7 7-7-7" stroke="#2e7d32" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function Passbook({ onBack }) {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [sortOrder, setSortOrder] = useState('newest');
  const [currentPage, setCurrentPage] = useState(1);
  const listRef = useRef(null);
  const { walletBalance } = useContext(WalletContext);

  useEffect(() => {
    const fetchTransactions = async () => {
      try {
        setLoading(true);
        const user = JSON.parse(localStorage.getItem('user'));
        if (!user || !user.id) {
          setError('User not found. Please login again.');
          return;
        }
        const response = await fetch(
          `https://backend.mainbazar777.site/api/user-transactions/${user.id}`
        );
        if (!response.ok) throw new Error('Failed to fetch transactions');
        const data = await response.json();
        if (!data.success) throw new Error(data.message || 'Failed to fetch transactions');

        // Combine both user_transactions and amount_transactions
        const allTransactions = [];
        
        if (data.user_transactions && Array.isArray(data.user_transactions)) {
          allTransactions.push(...data.user_transactions.map(t => ({ ...t, _type: 'user' })));
        }
        
        if (data.amount_transactions && Array.isArray(data.amount_transactions)) {
          allTransactions.push(...data.amount_transactions.map(t => ({ ...t, _type: 'amount' })));
        }

        if (allTransactions.length === 0) {
          throw new Error('No transactions found');
        }

        setTransactions(allTransactions);
      } catch (err) {
        console.error('Error fetching transactions:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchTransactions();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [filterType, sortOrder]);

  const typeOptions = ['all', ...Array.from(new Set(transactions.map(t => getTransactionType(t)).filter(Boolean)))];

  const filtered = transactions
    .filter(t => filterType === 'all' || getTransactionType(t) === filterType)
    .sort((a, b) => {
      const diff = new Date(b.created_at) - new Date(a.created_at);
      return sortOrder === 'newest' ? diff : -diff;
    });

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const pageItems = filtered.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  const goToPage = (p) => {
    setCurrentPage(p);
    listRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="pb-page">
      {/* Header */}
      <header className="pb-header">
        <button className="pb-back-btn" onClick={onBack}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M15 18L9 12L15 6" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <h1 className="pb-title">PassBook</h1>
        <div style={{ width: 36 }} />
      </header>

      {/* Filters */}
      <div className="pb-filter-bar">
        <select
          className="pb-select"
          value={filterType}
          onChange={e => setFilterType(e.target.value)}
        >
          {typeOptions.map(t => (
            <option key={t} value={t}>
              {t === 'all' ? 'All Types' : getTypeLabel(t)}
            </option>
          ))}
        </select>
        <select
          className="pb-select"
          value={sortOrder}
          onChange={e => setSortOrder(e.target.value)}
        >
          <option value="newest">Newest First</option>
          <option value="oldest">Oldest First</option>
        </select>
      </div>

      {!loading && !error && (
        <p className="pb-count">
          {filtered.length} transaction{filtered.length !== 1 ? 's' : ''}
          {totalPages > 1 && ` · Page ${currentPage} of ${totalPages}`}
        </p>
      )}

      {/* Content */}
      {loading ? (
        <div className="pb-center">
          <div className="pb-spinner" />
          <p className="pb-hint">Loading transactions…</p>
        </div>
      ) : error ? (
        <div className="pb-center">
          <svg width="44" height="44" viewBox="0 0 48 48" fill="none">
            <circle cx="24" cy="24" r="20" stroke="#e53e3e" strokeWidth="2"/>
            <path d="M24 16v8M24 32h.01" stroke="#e53e3e" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          <p className="pb-error-text">{error}</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="pb-center">
          <svg width="44" height="44" viewBox="0 0 48 48" fill="none">
            <rect x="8" y="8" width="32" height="32" rx="4" stroke="#8a9ab0" strokeWidth="2"/>
            <path d="M14 18h20M14 24h14" stroke="#8a9ab0" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          <p className="pb-hint">No transactions found</p>
        </div>
      ) : (
        <>
          <div className="pb-list" ref={listRef}>
            {pageItems.map(tx => {
              const debit = isDebit(tx.transaction_type, tx);
              const amountSign = debit ? '-' : '+';
              const amountFormatted = `${amountSign}₹${parseFloat(tx.amount || 0).toFixed(0)}`;
              
              // Handle reference display for both transaction types
              let ref = '';
              if (tx._type === 'amount') {
                // amount_transactions - show order_id or txn_id or utr_number
                ref = tx.order_id ? `#${tx.order_id}` : tx.txn_id ? `#${tx.txn_id}` : tx.utr_number ? `#${tx.utr_number}` : '#';
              } else {
                // user_transactions - show order_id, reference_id, or message
                ref = tx.order_id
                  ? `#${tx.order_id}`
                  : tx.reference_id
                  ? `#${tx.reference_id}`
                  : tx.message || '#';
              }

              // Get status badge text
              let statusText = '—';
              if (tx._type === 'amount') {
                statusText = tx.status_message || (tx.status === 1 ? 'APPROVED' : 'PENDING');
              } else {
                statusText = tx.status ? tx.status.toUpperCase() : '—';
              }

              return (
                <div key={`${tx._type}-${tx.id}`} className="pb-card">
                  {/* Icon */}
                  <div className={`pb-icon-wrap ${debit ? 'debit' : 'credit'}`}>
                    <ArrowIcon debit={debit} />
                  </div>

                  {/* Middle */}
                  <div className="pb-mid">
                    <span className="pb-label">{getTypeLabel(getTransactionType(tx), tx)}</span>
                    <span className="pb-date">{formatDateTime(tx.created_at)}</span>
                    {ref && <span className="pb-ref">{ref}</span>}
                  </div>

                  {/* Right */}
                  <div className="pb-right">
                    <span className={`pb-amount ${debit ? 'debit' : 'credit'}`}>{amountFormatted}</span>
                    <span className={`pb-badge ${getBadgeClass(tx.status, tx)}`}>
                      {statusText}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pb-pagination">
              <button
                className="pb-pg-btn"
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
              >
                ‹
              </button>
              {buildPageNumbers(currentPage, totalPages).map((p, i) =>
                p === '…' ? (
                  <span key={`e-${i}`} className="pb-pg-ellipsis">…</span>
                ) : (
                  <button
                    key={p}
                    className={`pb-pg-btn ${currentPage === p ? 'pb-pg-active' : ''}`}
                    onClick={() => goToPage(p)}
                  >
                    {p}
                  </button>
                )
              )}
              <button
                className="pb-pg-btn"
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                ›
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default Passbook;
